import {
  Button,
  CheckInput,
  DateInput,
  Select,
  TextInput,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Col, Row } from 'reactstrap';

import { SectionTitle } from '../../../../../components/text';
import { PathsLayouts } from '../../../../../config/routes';
import { useValidations } from '../../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../../locales/translations';
import { ModalityCodesEnum } from '../../../../SectionsMaintainer/enums/modality-codes.enum';
import { publishAcademicOffer, updateAcademicOfferSummary } from '../../api';
import { useAcademicOfferWizard } from '../../context/AcademicOfferWizardContext';
import { CeAcademicOfferStatus, TuitionProcess } from '../../types';
import { useAcademicOfferSummary } from './loaders/AcademicOfferSummaryLoader';

interface FormValues {
  tuitionProcess: { label: string; value: number } | null;
  year: string;
  classStartDate: string;
  classEndDate: string;
  enrollmentStartDate: string;
  enrollmentEndDate: string;
  offerLink: string;
  noEnrollmentRequired: boolean;
  completionDaysLimit: string;
}

interface Props {
  deliveryMode: string | null;
}

export function AcademicOfferRequiredDataForm({ deliveryMode }: Props) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const history = useHistory();
  const { ceAcademicOfferId, offerStatus } = useAcademicOfferWizard();
  const { data: summaryData } = useAcademicOfferSummary();
  const { msgValidations } = useValidations();

  const isActive = offerStatus === CeAcademicOfferStatus.Activa;
  const isClosed = offerStatus === CeAcademicOfferStatus.Cerrada;

  const isOnlineAutoAttended = deliveryMode === ModalityCodesEnum.OA;

  const ceProcessOptions = useMemo(
    () =>
      (summaryData?.ceProcesses ?? []).map((p: TuitionProcess) => ({
        label: p.name,
        value: p.id,
      })),
    [summaryData],
  );

  const defaultTuitionProcessOption = useMemo(() => {
    const typeId = summaryData?.offer?.tuitionProcessTypeId;
    if (!typeId) {
      return null;
    }
    const process = (summaryData?.ceProcesses ?? []).find(
      (p: TuitionProcess) => p.tuitionProcessType?.id === typeId,
    );
    if (!process) {
      return null;
    }
    return { label: process.name, value: process.id };
  }, [summaryData]);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      tuitionProcess: defaultTuitionProcessOption,
      year:
        summaryData?.offer.academicOfferStudyPlanVersionOffers?.[0]?.duration ??
        '',
      classStartDate:
        summaryData?.offer.academicOfferStudyPlanVersionOffers?.[0]
          ?.startDate ?? '',
      classEndDate:
        summaryData?.offer.academicOfferStudyPlanVersionOffers?.[0]?.endDate ??
        '',
      enrollmentStartDate: summaryData?.offer.enrollmentStartDate ?? '',
      enrollmentEndDate: summaryData?.offer.enrollmentEndDate ?? '',
      offerLink: summaryData?.offer.offerLink ?? '',
      noEnrollmentRequired: summaryData?.offer.noEnrollmentRequired ?? false,
      completionDaysLimit:
        summaryData?.offer.academicOfferStudyPlanVersionOffers?.[0]?.completionDaysLimit?.toString() ??
        '',
    },
  });

  const noEnrollmentRequired = useWatch({
    control,
    name: 'noEnrollmentRequired',
  });

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!ceAcademicOfferId) return;

      const basePayload = {
        year: parseInt(values.year, 10),
        offerLink: values.offerLink || undefined,
        noEnrollmentRequired: values.noEnrollmentRequired,
        ...(isOnlineAutoAttended
          ? {
              completionDaysLimit: values.completionDaysLimit
                ? parseInt(values.completionDaysLimit, 10)
                : undefined,
            }
          : {
              classStartDate: values.classStartDate || undefined,
              classEndDate: values.classEndDate || undefined,
            }),
        ...(!values.noEnrollmentRequired
          ? {
              enrollmentStartDate: values.enrollmentStartDate || undefined,
              enrollmentEndDate: values.enrollmentEndDate || undefined,
            }
          : {}),
      };

      const response = isActive
        ? await updateAcademicOfferSummary(ceAcademicOfferId, basePayload)
        : values.tuitionProcess
        ? await publishAcademicOffer(ceAcademicOfferId, {
            ...basePayload,
            tuitionProcessId: values.tuitionProcess.value,
          })
        : null;

      if (!response) return;

      if (!response.error && response.data) {
        addToast({
          icon: 'success',
          color: 'success',
          text: isActive
            ? t('summary.publish.updateSuccessMessage')
            : t('summary.publish.successMessage'),
        });
        history.push(`${PathsLayouts.continuousEducation}/academic-offers`);
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: isActive
            ? t('summary.publish.updateErrorMessage')
            : t('summary.publish.errorMessage'),
        });
      }
    },
    [ceAcademicOfferId, history, isActive, isOnlineAutoAttended, t],
  );

  const onConfirmSubmit = useCallback(
    (values: FormValues) => {
      showDialogConfirm({
        icon: {
          name: 'information',
          color: 'primary',
        },
        title: t('summary.publish.confirm.title'),
        subtitle: t('summary.publish.confirm.subtitle'),
        btnConfirm: {
          text: t('summary.publish.confirm.confirm'),
          color: 'primary',
          onConfirm: () => onSubmit(values),
        },
        btnClose: {
          text: t('common:actions.cancel'),
        },
      });
    },
    [onSubmit, t],
  );

  return (
    <>
      <SectionTitle text={t('summary.publish.title')} />
      <p className="mb-4"></p>

      <form onSubmit={(e) => e.preventDefault()}>
        <Row className="mb-3">
          <Col xs={12} md={6} lg={5}>
            <Select
              name="tuitionProcess"
              label={t('summary.publish.processType')}
              options={ceProcessOptions}
              control={control}
              rules={{ validate: (v) => !!v || msgValidations.required }}
              disabled={isActive || isClosed}
            />
          </Col>

          <Col xs={12} md={6} lg={2}>
            <TextInput
              name="year"
              label={t('summary.publish.year')}
              control={control}
              formatter={(value) => value.replace(/\D/g, '')}
              rules={{ required: msgValidations.required }}
              disabled={isClosed}
            />
          </Col>

          <Col xs={12} md={6} lg={5}>
            <TextInput
              name="offerLink"
              label={t('summary.publish.offerLink')}
              control={control}
              disabled={isClosed}
            />
          </Col>
        </Row>

        <Row className="mb-3">
          {!isOnlineAutoAttended && (
            <>
              <Col
                xs={12}
                md={6}
                lg={noEnrollmentRequired ? 6 : 3}
                className="mb-3"
              >
                <DateInput
                  name="classStartDate"
                  label={t('summary.publish.classStartDate')}
                  control={control}
                  rules={{ required: msgValidations.required }}
                  disabled={isClosed}
                />
              </Col>
              <Col
                xs={12}
                md={6}
                lg={noEnrollmentRequired ? 6 : 3}
                className="mb-3"
              >
                <DateInput
                  name="classEndDate"
                  label={t('summary.publish.classEndDate')}
                  control={control}
                  rules={{ required: msgValidations.required }}
                  disabled={isClosed}
                />
              </Col>
            </>
          )}
          {!noEnrollmentRequired && (
            <>
              <Col
                xs={12}
                md={6}
                lg={isOnlineAutoAttended ? 4 : 3}
                className="mb-3"
              >
                <DateInput
                  name="enrollmentStartDate"
                  label={t('summary.publish.enrollmentStartDate')}
                  control={control}
                  rules={{ required: msgValidations.required }}
                  disabled={isClosed}
                />
              </Col>
              <Col
                xs={12}
                md={6}
                lg={isOnlineAutoAttended ? 4 : 3}
                className="mb-3"
              >
                <DateInput
                  name="enrollmentEndDate"
                  label={t('summary.publish.enrollmentEndDate')}
                  control={control}
                  rules={{ required: msgValidations.required }}
                  disabled={isClosed}
                />
              </Col>
            </>
          )}
          {isOnlineAutoAttended && (
            <Col xs={12} md={6} lg={noEnrollmentRequired ? 5 : 4}>
              <TextInput
                name="completionDaysLimit"
                label={t('summary.publish.completionDaysLimit')}
                control={control}
                formatter={(value) => value.replace(/\D/g, '')}
                disabled={isClosed}
              />
            </Col>
          )}
        </Row>

        <Row className="mb-3">
          <Col xs={12}>
            <CheckInput
              name="noEnrollmentRequired"
              label={
                isOnlineAutoAttended
                  ? t('summary.publish.noEnrollmentRequiredOA')
                  : t('summary.publish.noEnrollmentRequired')
              }
              control={control}
              disabled={isClosed}
            />
          </Col>
        </Row>

        <Row>
          <Col xs={12} md={6} lg={4} className="offset-md-6 offset-lg-8">
            <Row>
              <Col>
                <Button
                  text={t('summary.publish.cancel')}
                  outlined
                  fullwidth
                  onClick={() =>
                    history.push(
                      `${PathsLayouts.continuousEducation}/academic-offers`,
                    )
                  }
                />
              </Col>
              <Col>
                {!isClosed && (
                  <Button
                    text={
                      isActive
                        ? t('summary.publish.save')
                        : t('summary.publish.submit')
                    }
                    type="button"
                    loading={isSubmitting}
                    fullwidth
                    onClick={
                      isActive
                        ? handleSubmit(onSubmit)
                        : handleSubmit(onConfirmSubmit)
                    }
                  />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </form>
    </>
  );
}
